
import React, { useState, useMemo } from 'react';
import { TOKEN_ICONS, CHAIN_ICONS, CEX_ICONS } from '../constants/icons';

// ─── Gradient palette for letter avatars ─────────────────────────────────────
const PALETTE = [
  ['#2E9CD6', '#1C7FB3'],
  ['#FF7A47', '#E8622F'],
  ['#23B26D', '#178C55'],
  ['#DA9A2B', '#B87F1E'],
  ['#9B5CF6', '#7C3AED'],
  ['#EC4899', '#BE185D'],
  ['#14B8A6', '#0D9488'],
  ['#F97316', '#C2410C'],
];

function paletteFor(key = '') {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
}

// ─── Trust Wallet asset repo ─────────────────────────────────────────────────
// Covers ~10,000+ tokens by chain + contract address. Chain IDs:
const TW_CHAIN_ID = {
  Ethereum:    'ethereum',
  'BNB Chain': 'smartchain',
  Polygon:     'polygon',
  Arbitrum:    'arbitrum',
  Optimism:    'optimism',
  Base:        'base',
  Avalanche:   'avalanchec',
  Fantom:      'fantom',
  Gnosis:      'xdai',
  Tron:        'tron',
  Solana:      'solana',
  TON:         'ton',
  Sui:         'sui',
  Aptos:       'aptos',
  Near:        'near',
};

// ─── CoinGecko symbol → coin ID (for tokens not in Trust Wallet) ──────────────
const CG_IDS = {
  BTC: 'bitcoin', ETH: 'ethereum', SOL: 'solana', BNB: 'binancecoin',
  XRP: 'ripple', ADA: 'cardano', DOGE: 'dogecoin', AVAX: 'avalanche-2',
  TRX: 'tron', LINK: 'chainlink', DOT: 'polkadot', MATIC: 'matic-network',
  POL: 'matic-network', LTC: 'litecoin', BCH: 'bitcoin-cash', XLM: 'stellar',
  UNI: 'uniswap', ATOM: 'cosmos', XMR: 'monero', ETC: 'ethereum-classic',
  TON: 'the-open-network', SUI: 'sui', APT: 'aptos', NEAR: 'near',
  ALGO: 'algorand', VET: 'vechain', FIL: 'filecoin', HBAR: 'hedera-hashgraph',
  AAVE: 'aave', MKR: 'maker', CRV: 'curve-dao-token', SNX: 'havven',
  COMP: 'compound-governance-token', YFI: 'yearn-finance', SUSHI: 'sushi',
  '1INCH': '1inch', BAL: 'balancer', LDO: 'lido-dao', RPL: 'rocket-pool',
  ARB: 'arbitrum', OP: 'optimism', STRK: 'starknet', ZK: 'zksync',
  MNT: 'mantle', IMX: 'immutable-x', RUNE: 'thorchain', CACAO: 'cacao',
  DASH: 'dash', ZEC: 'zcash', XTZ: 'tezos', STX: 'blockstack',
  KAS: 'kaspa', HYPE: 'hyperliquid', BERA: 'berachain-bgt',
  USDT: 'tether', USDC: 'usd-coin', DAI: 'dai', FRAX: 'frax',
  TUSD: 'true-usd', BUSD: 'binance-usd', LUSD: 'liquity-usd',
  CRVUSD: 'crvusd', GHO: 'gho', PYUSD: 'paypal-usd', USDS: 'usds',
  USDe: 'ethena-usde', FDUSD: 'first-digital-usd', BOLD: 'bold',
  WBTC: 'wrapped-bitcoin', WETH: 'weth', CBBTC: 'coinbase-wrapped-btc',
  LBTC: 'lombard-staked-btc', FBTC: 'fire-bitcoin',
  STETH: 'staked-ether', WSTETH: 'wrapped-staked-ether',
  RETH: 'rocket-pool-eth', CBETH: 'coinbase-wrapped-staked-eth',
  SHIB: 'shiba-inu', PEPE: 'pepe', FLOKI: 'floki', BONK: 'bonk',
  WIF: 'dogwifcoin', TRUMP: 'official-trump', PENGU: 'pudgy-penguins',
  POPCAT: 'popcat', MOODENG: 'moo-deng', NEIRO: 'neiro-on-eth',
  MOG: 'mog-coin', TURBO: 'turbo', SPX: 'spx6900',
  JUP: 'jupiter-exchange-solana', RAY: 'raydium', ORCA: 'orca',
  JTO: 'jito-governance-token', PYTH: 'pyth-network',
  RENDER: 'render-token', HNT: 'helium', MNDE: 'marinade',
  GRT: 'the-graph', FET: 'fetch-ai', OCEAN: 'ocean-protocol',
  TAO: 'bittensor', AGIX: 'singularitynet', NMR: 'numeraire',
  AXS: 'axie-infinity', SAND: 'the-sandbox', MANA: 'decentraland',
  ENJ: 'enjincoin', GALA: 'gala', ILV: 'illuvium', APE: 'apecoin',
  CRO: 'crypto-com-chain', OKB: 'okb', WOO: 'woo-network',
  PAXG: 'pax-gold', XAUT: 'tether-gold',
  AERO: 'aerodrome-finance', BRETT: 'brett', VIRTUAL: 'virtual-protocol',
  ENA: 'ethena', PENDLE: 'pendle', EIGEN: 'eigenlayer', ETHFI: 'ether-fi',
  ONDO: 'ondo-finance', W: 'wormhole', MORPHO: 'morpho',
  DYDX: 'dydx', GMX: 'gmx', GNS: 'gains-network',
  INJ: 'injective-protocol', SEI: 'sei-network', TIA: 'celestia',
  KUJI: 'kujira',
};

// ─── Build image URL waterfall for a given token ──────────────────────────────
function buildSources(symbol, chain, type) {
  if (!symbol) return [];

  const sym = symbol.toUpperCase();

  if (type === 'chain') {
    const url = CHAIN_ICONS[chain] || CHAIN_ICONS[symbol];
    return url ? [url] : [];
  }

  if (type === 'cex') {
    const url = CEX_ICONS[symbol.toLowerCase()];
    return url ? [url] : [];
  }

  const sources = [];

  // 1. Static map (highest priority -- always correct)
  if (TOKEN_ICONS[sym]) sources.push(TOKEN_ICONS[sym]);

  // 2. Trust Wallet assets repo (covers most ERC-20 / BEP-20 / SPL by symbol)
  const twChain = TW_CHAIN_ID[chain];
  if (twChain) {
    // Trust Wallet indexes by lowercase symbol for native/popular tokens
    sources.push(
      `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/${twChain}/assets/${sym}/logo.png`
    );
    // Also try lowercase variant
    sources.push(
      `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/${twChain}/assets/${sym.toLowerCase()}/logo.png`
    );
  }

  // 3. CoinGecko small image (reliable for top-1000, rate-limited for others)
  const cgId = CG_IDS[sym];
  if (cgId) {
    sources.push(
      `https://assets.coingecko.com/coins/images/search?query=${cgId}`
    );
    // Fallback to known CoinGecko image pattern by symbol (not always valid but worth trying)
    sources.push(
      `https://assets.coingecko.com/coins/images/search?query=${sym.toLowerCase()}`
    );
  }

  // 4. CryptoIcons (SVG fallback -- covers ~500 popular coins)
  sources.push(
    `https://cryptoicons.org/api/icon/${sym.toLowerCase()}/32`
  );

  // 5. Coinicons (another community-maintained set)
  sources.push(
    `https://raw.githubusercontent.com/coinwink/cryptocurrency-logos/master/coins/32x32/${sym.toLowerCase()}.png`
  );

  return sources;
}

// ─── Component ────────────────────────────────────────────────────────────────
const TokenImage = ({ symbol, chain, type = 'token', size = 40, className = '' }) => {
  const sources = useMemo(
    () => buildSources(symbol, chain, type),
    [symbol, chain, type]
  );

  const [idx, setIdx] = useState(0);

  // When symbol/chain/type changes, reset to first source
  const key = `${symbol}:${chain}:${type}`;
  const [lastKey, setLastKey] = useState(key);
  if (key !== lastKey) {
    setLastKey(key);
    setIdx(0);
  }

  const [c1, c2] = paletteFor(symbol || chain || '');
  const letter = symbol?.[0]?.toUpperCase() || '?';

  const avatarStyle = {
    width: size,
    height: size,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: size * 0.38,
    fontWeight: 700,
    color: '#fff',
    flexShrink: 0,
    userSelect: 'none',
  };

  // No sources at all -- render letter avatar immediately
  if (sources.length === 0 || idx >= sources.length) {
    return (
      <div className={`token-image-fallback ${className}`} style={avatarStyle}>
        {letter}
      </div>
    );
  }

  return (
    <img
      key={`${key}:${idx}`}   /* force re-mount when src changes */
      src={sources[idx]}
      alt={symbol}
      className={`token-image ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'cover',
        flexShrink: 0,
        background: '#1e293b',  /* placeholder bg while loading */
      }}
      onError={() => setIdx(i => i + 1)}  /* try next source on failure */
      onLoad={(e) => {
        // Some hosts return a 1x1 pixel on 404 -- detect and skip
        if (e.target.naturalWidth <= 1 || e.target.naturalHeight <= 1) {
          setIdx(i => i + 1);
        }
      }}
    />
  );
};

export default TokenImage;
